import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import logo from '@repo/media/logo/nljv/horizontal/NorthernLights_RGB_Horizontal_Teal.svg';

import { Button } from '@repo/components/button';
import { Callout } from '@repo/components/callout';

import { PageLoading } from '@repo/features/page-loading';

import { loginRequest } from './config.ts';
import msLogo from './microsoft-logo.svg';

// Entra's admin consent endpoint returns the administrator here with these parameters and no
// tokens. Consent and sign-in are separate flows, so the portal starts the sign-in itself.
type ConsentReturn = { consented: boolean; error?: string };

// Anyone can put text in the query string, and it would render under the Northern Lights logo.
// Only an Entra error description (AADSTS code first) is shown, and only its first line, capped.
const ENTRA_ERROR = /^AADSTS\d+:.*/;
const ERROR_MAX_LENGTH = 300;

function entraErrorDescription(params: URLSearchParams): string | undefined {
  const description = params.get('error_description')?.split('\n')[0]?.trim() ?? '';
  const match = ENTRA_ERROR.exec(description);
  return match ? match[0].slice(0, ERROR_MAX_LENGTH) : undefined;
}

function readConsentReturn(): ConsentReturn {
  const params = new URLSearchParams(globalThis.location.search);
  if (params.has('error')) {
    return { consented: false, error: entraErrorDescription(params) ?? 'Microsoft Entra ID reported an error.' };
  }
  return { consented: params.get('admin_consent')?.toLowerCase() === 'true' };
}

export function Login() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const consentReturn = useMemo(readConsentReturn, []);
  const startedRef = useRef(false);
  const [autoSignInFailed, setAutoSignInFailed] = useState(false);

  useEffect(() => {
    // The administrator already has an Entra session from consenting, so this round trip
    // completes without a prompt and they land in the portal signed in. Started once (strict
    // mode re-runs effects) and from a clean URL: MSAL returns to the page the request was
    // started from, and the consent parameters must not be there to trigger this again.
    if (consentReturn.consented && !isAuthenticated && inProgress === InteractionStatus.None && !startedRef.current) {
      startedRef.current = true;
      globalThis.history.replaceState(null, '', '/');
      // Stale MSAL state (interaction_in_progress) would otherwise leave the spinner up forever.
      instance.loginRedirect({ ...loginRequest }).catch(() => {
        setAutoSignInFailed(true);
      });
    }
  }, [consentReturn.consented, isAuthenticated, inProgress, instance]);

  if (isAuthenticated || inProgress !== InteractionStatus.None || (consentReturn.consented && !autoSignInFailed)) {
    return (
      <div className="flex items-center justify-center p-6 pt-20 w-full">
        <PageLoading />
      </div>
    );
  }

  return (
    <>
      <title>Sign in | Northern Lights</title>

      <div className="bg-secondary-50 flex flex-col pt-28 gap-12 items-center h-svh w-svw px-4">
        <img className="max-w-lg" src={logo} alt="Northern Lights" />
        {consentReturn.error && (
          <Callout variant="negative" title="Consent was not granted" className="max-w-xl">
            {consentReturn.error}
            {' '}
            Open the link from the invitation again, or contact Northern Lights with the error code.
          </Callout>
        )}
        <Button
          icon={{ component: <img src={msLogo} alt="Microsoft" /> }}
          action={async () => {
            await instance.loginRedirect({ ...loginRequest });
          }}
        >
          Sign in with Microsoft
        </Button>
      </div>
    </>
  );
}
